import api from 'api/axiosInstance';
import type { IWorkspace } from 'types/workspace.types';
import type { PagingV2Response } from 'types/pagination.types';
import { accountableTLD, taxCoachEmail } from 'utils/constants';

const rootPath = '/v3/access/clients';

// Types for request/response
export type InviteAccountantBody = {
  email: string;
};

export type InviteAccountantResponse = {
  ok: boolean;
  message: string;
};

export type InvitationActionResponse = {
  ok: boolean;
  message: string;
};

export type ErrorResponse = {
  message: string;
  slug: string;
};

// Types for GET endpoints
export type ClientWorkspacesResponse = {
  data: IWorkspace[];
  paging: PagingV2Response;
};

export type WorkspaceInvitation = {
  _id: string;
  from: string;
  to: string;
  role: string;
  token: string;
  created_at: string;
  modified: string;
  expires: string;
  status: InvitationStatus;
};

export type WorkspaceInvitationsResponse = {
  invitations: WorkspaceInvitation[];
  paging: PagingV2Response;
};

// sent invites to accountants
export enum InvitationStatus {
  Accepted = 'accepted',
  Pending = 'pending',
  Canceled = 'canceled',
}

export type PaginationParams = {
  page?: number;
  per_page?: number;
};

export type ClientWorkspacesParams = PaginationParams & {
  includeIntervatGrants?: boolean;
};

export type InvitationParams = PaginationParams & {
  status?: InvitationStatus;
};

export type ClientWorkspacesWithTaxCoachSplitResponse = {
  support: IWorkspace[];
  general: IWorkspace[];
  paging: PagingV2Response;
};

// POST /v3/access/clients/invitations
// Invite Accountant
export const inviteAccountant = (
  body: InviteAccountantBody,
): Promise<InviteAccountantResponse> =>
  api.post(`${rootPath}/invitations`, body);

// DELETE /v3/access/clients/invitations
// Decline Invitation
export const declineInvitation = (
  token: string,
): Promise<InvitationActionResponse> =>
  api.delete(`${rootPath}/invitations`, {
    params: { token },
  });

// PATCH /v3/access/clients/invitations
// Accept Invitation
export const acceptInvitation = (
  token: string,
): Promise<InvitationActionResponse> =>
  api.patch(
    `${rootPath}/invitations`,
    {},
    {
      params: { token },
    },
  );

// GET /v3/access/clients/invitations
// Returns workspace invitations sent by the client
export const getPendingSentInvitations = async (): Promise<
  WorkspaceInvitation[]
> => {
  return (
    (
      (await api.get(`${rootPath}/invitations`, {
        params: {
          page: 1,
          per_page: 100, // to avoid pagination for now
          status: InvitationStatus.Pending,
        },
      })) as WorkspaceInvitationsResponse
    )?.invitations || []
  );
};

// DELETE /v3/access/clients/workspaces/{wsId}
// Revoke Workspace Access
export const revokeWorkspaceAccess = (
  wsId: string,
): Promise<InvitationActionResponse> =>
  api.delete(`${rootPath}/workspaces/${wsId}`);

// GET /v3/access/clients/workspaces
// List Client Workspaces (includeIntervatGrants=true adds intervatGranted per workspace)
export const getClientWorkspaces = (
  params?: ClientWorkspacesParams,
): Promise<ClientWorkspacesResponse> =>
  api.get(`${rootPath}/workspaces`, {
    params: {
      page: 1,
      per_page: 25,
      ...params,
    },
  });

const caseInsensitiveCollation = { sensitivity: 'base' as const };

const getWorkspacePrimarySortLabel = (workspace: IWorkspace): string =>
  (
    workspace.name ||
    workspace.organization_name ||
    workspace.email ||
    ''
  ).trim();

const sortWorkspacesForDisplay = (workspaces: IWorkspace[]): IWorkspace[] =>
  [...workspaces].sort((a, b) => {
    const aPrimaryLabel = getWorkspacePrimarySortLabel(a);
    const bPrimaryLabel = getWorkspacePrimarySortLabel(b);
    const primaryLabelComparison = aPrimaryLabel.localeCompare(
      bPrimaryLabel,
      undefined,
      caseInsensitiveCollation,
    );
    if (primaryLabelComparison !== 0) return primaryLabelComparison;

    const emailComparison = a.email.localeCompare(
      b.email,
      undefined,
      caseInsensitiveCollation,
    );
    if (emailComparison !== 0) return emailComparison;

    return a._id.localeCompare(b._id);
  });

const sortPendingInvitationsForDisplay = (
  invitations: WorkspaceInvitation[],
): WorkspaceInvitation[] =>
  [...invitations].sort((a, b) => {
    const fromEmailComparison = a.from.localeCompare(
      b.from,
      undefined,
      caseInsensitiveCollation,
    );
    if (fromEmailComparison !== 0) return fromEmailComparison;

    return a._id.localeCompare(b._id);
  });

export const categorizeWorkspaces = (
  workspaces: IWorkspace[],
): {
  support: IWorkspace[];
  general: IWorkspace[];
} => {
  const support: IWorkspace[] = [];
  const general: IWorkspace[] = [];
  workspaces.forEach((workspace: IWorkspace) => {
    const emailDomain = workspace.email.split('@').pop();
    const isSupport = emailDomain === accountableTLD;
    if (isSupport) support.push({ ...workspace, email: taxCoachEmail });
    else general.push(workspace);
  });
  return {
    support: sortWorkspacesForDisplay(support),
    general: sortWorkspacesForDisplay(general),
  };
};

export const categorizePendingInvitations = (
  invitations: WorkspaceInvitation[],
): {
  support: WorkspaceInvitation[];
  general: WorkspaceInvitation[];
} => {
  const support: WorkspaceInvitation[] = [];
  const general: WorkspaceInvitation[] = [];
  invitations.forEach((invitation: WorkspaceInvitation) => {
    const emailDomain = invitation.from.split('@').pop();
    if (emailDomain === accountableTLD) support.push(invitation);
    else general.push(invitation);
  });
  return {
    support: sortPendingInvitationsForDisplay(support),
    general: sortPendingInvitationsForDisplay(general),
  };
};
